import { useFeatureGate } from '@statsig/expo-bindings';
import { router } from 'expo-router';
import { useState } from 'react';
import {
    ArrowRightIcon,
    BabyIcon,
    BadgeLabel,
    ChartLineIcon,
    CirclePlusIcon,
    ClipboardCheckIcon,
    Icon,
    IdCardLanyardIcon,
    LandmarkIcon,
    NavigationRow,
    Row,
    RowList,
    ScreenContainer,
    SegmentedControl,
    ShieldCheckIcon,
    Spinner,
    UserRoundIcon,
    XStack,
    YStack,
} from 'smoothie';

import { isEmpty } from '../../common/misc';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { getPartnerDisplayFirstName } from '../../common/utils/userUtils';
import { useTrackViewOnFocus } from '../../events/hooks/useTrackViewOnFocus';
import { FeatureFlags } from '../../feature-flags/FeatureFlags';
import type { User } from '../../user/types/User';
import {
    useGetUserFinancialProfileCompleteness,
    type UserFinancialProfileCompleteness,
} from '../hooks/useGetUserFinancialProfileCompleteness';

const IncompleteBadge = () => {
    return (
        <BadgeLabel
            size="large"
            text="Add"
            icon={CirclePlusIcon}
            variant="warning"
        />
    );
};

export const FinancialProfile = () => {
    const user = useAuthedUser();
    const [selectedUserId, setSelectedUserId] = useState<string>(user.id);
    const { value: enableIm } = useFeatureGate(FeatureFlags.ENABLE_IM_TAB);

    const isSelectedMemberCurrentUser = user.id === selectedUserId;

    const shouldShowUserToggle = user.partnerMember != null;

    const primaryMemberCompleteness =
        useGetUserFinancialProfileCompleteness(user);
    const partnerMemberCompleteness = useGetUserFinancialProfileCompleteness(
        (user.partnerMember ?? null) as User | null,
    );

    const isLoadingFinancialProfileCompleteness =
        primaryMemberCompleteness.isLoading ||
        partnerMemberCompleteness.isLoading;

    const selectedCompleteness: UserFinancialProfileCompleteness =
        selectedUserId === user.id
            ? primaryMemberCompleteness
            : partnerMemberCompleteness;

    const userToggleOptions = [user, user.partnerMember]
        .filter((u) => u != null)
        .map((u) => {
            const isPartner = u?.id !== user.id;
            const showDot = isPartner
                ? !partnerMemberCompleteness.isFinancialProfileComplete
                : !primaryMemberCompleteness.isFinancialProfileComplete;
            return {
                value: u?.id ?? '',
                label: isPartner
                    ? getPartnerDisplayFirstName(u?.firstName)
                    : (u?.firstName ?? ''),
                showDot,
            };
        });

    return (
        <ScreenContainer title="Financial profile" hasBackButton={true}>
            {isLoadingFinancialProfileCompleteness ? (
                <Spinner />
            ) : (
                <YStack gap={24} justifyContent="space-between">
                    {shouldShowUserToggle && (
                        <XStack justifyContent="center">
                            <SegmentedControl
                                selected={selectedUserId}
                                defaultValue={selectedUserId}
                                onValueChange={(val: string) => {
                                    if (!isEmpty(val)) {
                                        setSelectedUserId(val);
                                    }
                                }}
                                options={userToggleOptions}
                            />
                        </XStack>
                    )}
                    <IncomeSection
                        selectedUserId={selectedUserId}
                        selectedUserCompleteness={selectedCompleteness}
                    />
                    {enableIm && isSelectedMemberCurrentUser && (
                        <InvestmentProfileSection />
                    )}
                    <OtherDetailsSection
                        selectedUserId={selectedUserId}
                        selectedUserCompleteness={selectedCompleteness}
                    />
                </YStack>
            )}
        </ScreenContainer>
    );
};

const IncomeSection = ({
    selectedUserId,
    selectedUserCompleteness,
}: {
    selectedUserId: string;
    selectedUserCompleteness: UserFinancialProfileCompleteness;
}) => {
    const { isEmploymentComplete, isIncomeComplete } = selectedUserCompleteness;

    return (
        <RowList rowHeaderProps={{ title: 'Income' }}>
            <Row
                title="Employment status"
                subtitle="Add employment status"
                leftIcon={IdCardLanyardIcon}
                onPress={() => {
                    router.navigate({
                        pathname: '/profile/employment-status',
                        params: { userId: selectedUserId },
                    });
                }}
                rightContent={
                    isEmploymentComplete ? (
                        <Icon color="$black" icon={ArrowRightIcon} />
                    ) : (
                        <IncompleteBadge />
                    )
                }
            />
            <Row
                title="Income sources"
                subtitle="Add income sources"
                leftIcon={UserRoundIcon}
                onPress={() => {
                    router.navigate({
                        pathname: '/profile/income',
                        params: { userId: selectedUserId },
                    });
                }}
                rightContent={
                    isIncomeComplete ? (
                        <Icon color="$black" icon={ArrowRightIcon} />
                    ) : (
                        <IncompleteBadge />
                    )
                }
            />
        </RowList>
    );
};

const InvestmentProfileSection = () => {
    useTrackViewOnFocus('Profile/InvestmentProfile');
    return (
        <RowList rowHeaderProps={{ title: 'Investment' }}>
            <NavigationRow
                title="Risk profile"
                subtitle="Experience and risk level"
                leftIcon={ChartLineIcon}
                onPress={() => {
                    router.navigate('/profile/investment/risk');
                }}
            />
            <NavigationRow
                title="Background"
                subtitle="Political and investment exposure"
                leftIcon={ClipboardCheckIcon}
                onPress={() => {
                    router.navigate('/profile/investment/background');
                }}
            />
            <NavigationRow
                title="Trusted contact"
                subtitle="Contact information"
                leftIcon={ShieldCheckIcon}
                onPress={() => {
                    router.navigate('/profile/investment/trusted-contact');
                }}
            />
        </RowList>
    );
};

const OtherDetailsSection = ({
    selectedUserId,
    selectedUserCompleteness,
}: {
    selectedUserId: string;
    selectedUserCompleteness: UserFinancialProfileCompleteness;
}) => {
    const { isInsuranceComplete, isTaxComplete, isKidsComplete } =
        selectedUserCompleteness;

    return (
        <RowList rowHeaderProps={{ title: 'Other details' }}>
            <Row
                title="Insurance coverage"
                subtitle="Select insurance coverage"
                leftIcon={ClipboardCheckIcon}
                onPress={() => {
                    router.navigate({
                        pathname: '/profile/insurance',
                        params: { userId: selectedUserId },
                    });
                }}
                rightContent={
                    isInsuranceComplete ? (
                        <Icon color="$black" icon={ArrowRightIcon} />
                    ) : (
                        <IncompleteBadge />
                    )
                }
            />
            <Row
                title="Taxes"
                subtitle="Tax filing status"
                leftIcon={LandmarkIcon}
                onPress={() => {
                    router.navigate({
                        pathname: '/profile/taxes',
                        params: { userId: selectedUserId },
                    });
                }}
                rightContent={
                    isTaxComplete ? (
                        <Icon color="$black" icon={ArrowRightIcon} />
                    ) : (
                        <IncompleteBadge />
                    )
                }
            />
            <Row
                title="Kids"
                subtitle="Kids details"
                leftIcon={BabyIcon}
                onPress={() => {
                    router.navigate('/profile/kids');
                }}
                rightContent={
                    isKidsComplete ? (
                        <Icon color="$black" icon={ArrowRightIcon} />
                    ) : (
                        <IncompleteBadge />
                    )
                }
            />
        </RowList>
    );
};
